// useDeviceListData – load device mappings and TLS status.

import { useEffect, useState } from 'react';
import type { DeviceMasterRow } from '../../../../types/DeviceListTypes';
import { normalizeDeviceMappingRows } from '../../../helpers/deviceMappingRows';
import { formatApiResponseError } from '../../EventsManagementView/utils';
import { bustCache, withCache } from '../../../helpers/apiCache';

import type { DeviceListDataResult, UseDeviceListDataOptions } from '../types/DeviceListViewTypes';

const MAPPINGS_TTL = 5 * 60_000;
const TLS_TTL     = 10 * 60_000;

export type { DeviceListDataResult, UseDeviceListDataOptions };

export function useDeviceListData(options?: UseDeviceListDataOptions): DeviceListDataResult {
  const [rows, setRows] = useState<DeviceMasterRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [tlsMap, setTlsMap] = useState<Record<string, boolean>>({});
  const [tlsError, setTlsError] = useState<string | null>(null);
  const [tlsLoading, setTlsLoading] = useState<boolean>(false);
  const [tlsRetryKey, setTlsRetryKey] = useState(0);

  /** Load device mappings once on mount. */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (options?.skipDeviceMasterLoad) {
        setRows(Array.isArray(options.seedRows) ? options.seedRows : []);
        setColumns(Array.isArray(options.seedColumns) ? options.seedColumns : []);
        setLoading(false);
        setError(null);
        return;
      }

      if (!window.eventsAPI?.getDeviceMappings) {
        setError('eventsAPI.getDeviceMappings is not available');
        return;
      }

      if (options?.reloadKey) bustCache('mappings');

      setLoading(true);
      setError(null);

      try {
        const res = await withCache('mappings', MAPPINGS_TTL, () => window.eventsAPI.getDeviceMappings());
        if (cancelled) return;
        if (!res?.ok) {
          throw new Error(formatApiResponseError(res, 'Failed to load device mappings'));
        }

        const raw = Array.isArray(res?.data?.devices) ? res.data.devices : [];
        const { rows: normalizedRows, columns: normalizedColumns } = normalizeDeviceMappingRows(raw);

        setRows(normalizedRows);
        setColumns(normalizedColumns);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load device mappings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [options?.seedColumns, options?.seedRows, options?.skipDeviceMasterLoad, options?.reloadKey]);

  /** When rows/columns are available, check TLS provisioned per device. */
  useEffect(() => {
    let cancelled = false;

    async function checkTls() {
      if (options?.enableTlsCheck === false) {
        setTlsMap({});
        setTlsError(null);
        setTlsLoading(false);
        return;
      }

      if (!rows.length) return;
      if (!columns.length) return;
      if (!window.eventsAPI?.queryDeviceTls) return;

      const scopedIds = options?.tlsDeviceIds;
      const ids = Array.isArray(scopedIds)
        ? scopedIds.map((id) => String(id || '').trim()).filter((id) => id.length > 0)
        : rows
            .map((row) => row[columns[0]])
            .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);

      if (!ids.length) return;

      if (options?.reloadKey) bustCache('tls');

      setTlsLoading(true);
      setTlsError(null);

      try {
        const res = await withCache('tls', TLS_TTL, () => window.eventsAPI.queryDeviceTls(ids));
        if (cancelled) return;
        if (!res?.ok) {
          throw new Error(formatApiResponseError(res, 'Failed to check TLS status'));
        }
        const provisionedMap = res?.data?.provisioned;
        setTlsMap(provisionedMap || {});
      } catch (err: any) {
        if (cancelled) return;
        setTlsError(err?.message || 'Failed to check TLS status');
      } finally {
        if (!cancelled) setTlsLoading(false);
      }
    }

    void checkTls();

    return () => {
      cancelled = true;
    };
  }, [rows, columns, options?.tlsDeviceIds, options?.reloadKey, tlsRetryKey]);

  return {
    rows,
    columns,
    loading,
    error,
    tlsMap,
    tlsLoading,
    tlsError,
    retryTls: () => { bustCache('tls'); setTlsRetryKey((k) => k + 1); },
  };
}
